import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchMe();
      setBootstrapping(false);
    })();
  }, [fetchMe]);

  const login = useCallback(async ({ email, password, remember_me }) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, remember_me });
      if (data?.access_token) {
        if (remember_me) localStorage.setItem("callisto_token", data.access_token);
        else sessionStorage.setItem("callisto_token", data.access_token);
        // Always also set localStorage for axios interceptor convenience
        localStorage.setItem("callisto_token", data.access_token);
      }
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      // ignore
    }
    localStorage.removeItem("callisto_token");
    sessionStorage.removeItem("callisto_token");
    setUser(false);
  }, []);

  const value = {
    user,
    bootstrapping,
    isAuthenticated: !!user && user !== false,
    login,
    logout,
    refresh: fetchMe,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
